import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logo from "../assets/wtei-full-logo.png";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/#about" },
  { label: "What We Do", to: "/#what-we-do" },
  { label: "Our Brands", to: "/#brands" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 250);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/10 py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <button
          data-testid="logo-link"
          onClick={() => handleNav("/")}
          className="group flex items-center gap-3"
        >
          <img
            src={logo}
            alt="WTEI — World Travel Excellence Institute"
            className={`rounded-lg bg-white transition-all duration-500 ${scrolled ? "h-12 md:h-14" : "h-14 md:h-16"} w-auto shrink-0`}
          />
          <span className="hidden md:block font-heading text-lg lg:text-xl leading-tight text-wtei-text whitespace-nowrap">
            World Travel <span className="text-wtei-primary">Excellence</span> Institute
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            <button
              key={item.label}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => handleNav(item.to)}
              className="text-sm uppercase tracking-[0.15em] text-wtei-text/80 hover:text-wtei-primary transition-colors duration-300"
            >
              {item.label}
            </button>
          ))}
          <button
            data-testid="header-contact-btn"
            onClick={() => handleNav("/#contact")}
            className="bg-transparent border border-wtei-primary text-wtei-primary px-7 py-2.5 uppercase tracking-widest text-xs hover:bg-wtei-primary hover:text-black transition-all duration-300"
          >
            Contact
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-wtei-text"
          onClick={() => setOpen(!open)}
        >
          {open ? <X strokeWidth={1} size={28} /> : <Menu strokeWidth={1} size={28} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden mt-4 px-6 pb-6 flex flex-col gap-5 bg-black/90 backdrop-blur-xl border-t border-white/10 pt-6">
          {NAV.map((item) => (
            <button
              key={item.label}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => handleNav(item.to)}
              className="text-left text-base uppercase tracking-[0.15em] text-wtei-text/80 hover:text-wtei-primary transition-colors"
            >
              {item.label}
            </button>
          ))}
          <button
            data-testid="mobile-contact-btn"
            onClick={() => handleNav("/#contact")}
            className="border border-wtei-primary text-wtei-primary px-7 py-3 uppercase tracking-widest text-xs hover:bg-wtei-primary hover:text-black transition-all"
          >
            Contact
          </button>
        </div>
      )}
    </header>
  );
};
